/**
 * SSR-safe GA4 analytics wrapper.
 *
 * The base gtag.js tag (Measurement ID G-DE8R7HM8WV) and automatic page_view
 * tracking are installed in `src/routes/__root.tsx`. This module only fires
 * custom conversion/engagement events and never re-initializes the tag or
 * duplicates page_view tracking.
 *
 * Every public function no-ops when `window.gtag` is unavailable (e.g. during
 * SSR or before the gtag script has loaded), so it is safe to call from
 * render-adjacent code and event handlers alike.
 *
 * No personally identifiable information (PII) is ever sent to GA4 from here:
 * only non-identifying parameters such as form id/name and the page path.
 */

type GtagCommand = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagCommand;
    dataLayer?: unknown[];
  }
}

/** Current page path, or empty string when not in a browser. */
function currentPath(): string {
  return typeof window !== "undefined" ? window.location.pathname : "";
}

/** Resolve or fallback-create the gtag function. */
function getGtag(): GtagCommand | undefined {
  if (typeof window === "undefined") return undefined;

  // 1. Direct window.gtag reference
  if (typeof window.gtag === "function") {
    return window.gtag;
  }

  // 2. DataLayer fallback array-push function if window.gtag hasn't bound yet
  if (Array.isArray(window.dataLayer)) {
    const fallbackGtag: GtagCommand = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
    return fallbackGtag;
  }

  return undefined;
}

/**
 * Fire a GA4 event.
 */
export function trackEvent(
  name: string,
  params: Record<string, unknown> = {},
): void {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", name, params);
}

/**
 * Fire `generate_lead` after a quote form submission has been accepted.
 *
 * Call this ONLY once the form considers the submission successful — never on
 * button click or on validation failure. Sends only non-PII identifiers.
 */
export function trackGenerateLead(params: {
  form_id: string;
  form_name: string;
}): void {
  trackEvent("generate_lead", {
    form_id: params.form_id,
    form_name: params.form_name,
    form_location: currentPath(),
  });
}

/**
 * Fire `click_to_call` when a visitor clicks a `tel:` link.
 * Sends only the page path — never the visitor's information.
 */
export function trackClickToCall(): void {
  trackEvent("click_to_call", { page_location: currentPath() });
}

/**
 * Fire `click_to_email` when a visitor clicks a `mailto:` link.
 * The email address itself is intentionally NOT sent.
 */
export function trackClickToEmail(): void {
  trackEvent("click_to_email", { page_location: currentPath() });
}

/**
 * Fire `view_form` (engagement only, not a lead) when the quote modal opens.
 */
export function trackViewForm(params: {
  form_id: string;
  form_name: string;
}): void {
  trackEvent("view_form", {
    form_id: params.form_id,
    form_name: params.form_name,
    form_location: currentPath(),
  });
}

/**
 * Fire `chat_open` (engagement only) when the chat widget is opened.
 */
export function trackChatOpen(): void {
  trackEvent("chat_open", { page_location: currentPath() });
}

/**
 * Install a single delegated document-level listener that fires
 * `click_to_call` for any `tel:` anchor and `click_to_email` for any
 * `mailto:` anchor across the whole site. This avoids per-link wiring and
 * automatically covers the fixed CTA "Call" button once it is a real `tel:`
 * link.
 *
 * Returns a cleanup function that removes the listener.
 */
export function initLeadClickTracking(): () => void {
  if (typeof document === "undefined") return () => {};

  const handler = (event: MouseEvent) => {
    const target = event.target as Element | null;
    if (!target || typeof target.closest !== "function") return;
    const anchor = target.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") ?? "";
    if (href.startsWith("tel:")) {
      trackClickToCall();
    } else if (href.startsWith("mailto:")) {
      trackClickToEmail();
    }
  };

  document.addEventListener("click", handler, { capture: true });
  return () =>
    document.removeEventListener("click", handler, { capture: true });
}
